import React, { useCallback, useEffect, useMemo, useState } from "react";
import "../styles/AddUserModal.css";
import { getMarketSymbols } from "../services/api";
import TradingPairsSelector, { normalizeTradingPairs } from "./TradingPairsSelector";

const AddUserModal = ({
  open,
  onClose,
  onCreate,
  loading,
  error,
  success,
}) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [tradingPairs, setTradingPairs] = useState([]);
  const [tradeMode, setTradeMode] = useState("AUTO");
  const [apiKey, setApiKey] = useState("");
  const [apiSecret, setApiSecret] = useState("");
  const [telegramChatIds, setTelegramChatIds] = useState("");
  const [dashboardView, setDashboardView] = useState("BOTH");
  const [showPassword, setShowPassword] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [symbolOptions, setSymbolOptions] = useState([]);
  const [symbolsLoading, setSymbolsLoading] = useState(false);
  const [symbolsError, setSymbolsError] = useState("");

  useEffect(() => {
    if (open) {
      setEmail("");
      setPassword("");
      setTradingPairs([]);
      setTradeMode("AUTO");
      setApiKey("");
      setApiSecret("");
      setTelegramChatIds("");
      setDashboardView("BOTH");
      setShowPassword(false);
      setShowSecret(false);
    }
  }, [open]);

  const hasSymbolOptions = useMemo(() => {
    if (Array.isArray(symbolOptions)) return symbolOptions.length > 0;
    return Boolean(symbolOptions?.spot_symbols?.length || symbolOptions?.futures_symbols?.length || symbolOptions?.symbols?.length);
  }, [symbolOptions]);

  const loadSymbols = useCallback(async (force = false) => {
    if (symbolsLoading) return;
    if (!force && hasSymbolOptions) return;
    setSymbolsLoading(true);
    setSymbolsError("");
    const result = await getMarketSymbols();
    if (!result.success) {
      setSymbolsError(result.error || "No se pudo cargar los pares");
      setSymbolOptions([]);
    } else {
      setSymbolOptions(result.data || {});
    }
    setSymbolsLoading(false);
  }, [hasSymbolOptions, symbolsLoading]);

  useEffect(() => {
    if (open) {
      void loadSymbols(false);
    }
  }, [open, loadSymbols]);

  const normalizedPairs = useMemo(() => normalizeTradingPairs(tradingPairs), [tradingPairs]);

  if (!open) return null;

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!email || !password) return;
    const primarySymbol = normalizedPairs[0]?.symbol || "";
    onCreate?.({
      email,
      password,
      symbol: primarySymbol,
      tradingPairs: normalizedPairs,
      tradeMode: tradeMode === "AUTO" ? null : tradeMode,
      pionexApiKey: apiKey,
      pionexApiSecret: apiSecret,
      telegramChatIds,
      dashboardView,
    });
  };

  return (
    <div className="add-user-backdrop" onClick={() => onClose?.()}>
      <div className="add-user-modal" onClick={(event) => event.stopPropagation()}>
        <header className="add-user-header">
          <div>
            <h3>Agregar usuario</h3>
            <p>Se enviara un correo con las credenciales.</p>
          </div>
          <button
            type="button"
            className="add-user-close"
            onClick={() => onClose?.()}
            aria-label="Cerrar"
          >
            ✕
          </button>
        </header>

        {error ? <div className="add-user-alert error">{error}</div> : null}
        {success ? <div className="add-user-alert success">{success}</div> : null}

        <form className="add-user-form" onSubmit={handleSubmit}>
          <label className="add-user-field">
            <span>Email</span>
            <input
              type="email"
              placeholder="usuario@dominio.com"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
              disabled={loading}
            />
          </label>

          <label className="add-user-field">
            <span>Pares de operación</span>
            <TradingPairsSelector
              value={tradingPairs}
              onChange={setTradingPairs}
              symbolOptions={symbolOptions}
              symbolsLoading={symbolsLoading}
              symbolsError={symbolsError}
              onRefreshSymbols={() => loadSymbols(true)}
              disabled={loading}
            />
          </label>

          <label className="add-user-field">
            <span>Modo</span>
            <select
              value={tradeMode}
              onChange={(event) => setTradeMode(event.target.value)}
              disabled={loading}
            >
              <option value="AUTO">Auto (según entorno)</option>
              <option value="SIM">Simulación</option>
              <option value="REAL">Producción</option>
            </select>
          </label>

          <label className="add-user-field">
            <span>Vista</span>
            <select
              value={dashboardView}
              onChange={(event) => setDashboardView(event.target.value)}
              disabled={loading}
            >
              <option value="BOTH">Ambos</option>
              <option value="TRADE">Solo trade</option>
            </select>
          </label>

          <label className="add-user-field">
            <span>Clave</span>
            <div className="add-user-password">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Minimo 6 caracteres"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
                minLength={6}
                disabled={loading}
              />
              <button
                type="button"
                className="add-user-toggle"
                onClick={() => setShowPassword((prev) => !prev)}
                disabled={loading}
              >
                {showPassword ? "Ocultar" : "Ver"}
              </button>
            </div>
          </label>

          <label className="add-user-field">
            <span>Pionex API Key</span>
            <input
              type="text"
              placeholder="Opcional"
              value={apiKey}
              onChange={(event) => setApiKey(event.target.value)}
              disabled={loading}
            />
          </label>

          <label className="add-user-field">
            <span>Pionex API Secret</span>
            <div className="add-user-password">
              <input
                type={showSecret ? "text" : "password"}
                placeholder="Opcional"
                value={apiSecret}
                onChange={(event) => setApiSecret(event.target.value)}
                disabled={loading}
              />
              <button
                type="button"
                className="add-user-toggle"
                onClick={() => setShowSecret((prev) => !prev)}
                disabled={loading}
              >
                {showSecret ? "Ocultar" : "Ver"}
              </button>
            </div>
          </label>

          <label className="add-user-field">
            <span>Telegram Chat IDs</span>
            <input
              type="text"
              placeholder="Ej: 123456789, 987654321"
              value={telegramChatIds}
              onChange={(event) => setTelegramChatIds(event.target.value)}
              disabled={loading}
            />
          </label>

          <div className="add-user-actions">
            <button type="button" className="add-user-secondary" onClick={() => onClose?.()} disabled={loading}>
              Cancelar
            </button>
            <button type="submit" className="add-user-primary" disabled={loading}>
              {loading ? "Creando..." : "Crear usuario"}
            </button>
          </div>
        </form>

      </div>
    </div>
  );
};

export default AddUserModal;
